import React, { createRef, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { getIndex } from '../../blocks/ContentWrapper/ContentWrapper.utils';
import { computeState, focusHeadlineFromRefAndOutlineBlock, idCreator, Views } from './BlockInteractions.utils';
import { BlockInteractionContextProvider } from './BlockInteractionContext';
import { useBlockInteractions } from './useBlockInteractions';
import { BlockInteractionsProps } from './BlockInteractionsTypes';
import { BlockInteractionsBar } from './partials/BlockInteractionsBar';
import { ContentWrapperContainer } from './partials/ContentWrapperContainer';
import { BlockInteractionsInstantInteraction } from './partials/BlockInteractionsInstantInteraction';
import { BlockInteractionsContent } from './partials/BlockInteractionsContent';
import { DefaultView } from './partials/DefaultView';
import { BlockInteractionsLoadingSpinner } from './partials/BlockInteractionsLoadingSpinner';
import { BlockInteractionsInfo } from './partials/BlockInteractionsInfo';
import { BlockInteractionsButtons } from './partials/BlockInteractionsButtons';
import { SecondaryView } from './partials/SecondaryView';
import { EditView } from './partials/EditView';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import bem from '../../../lib/bemUtils/bem';

/** Component for managing Views of any block component.
 *
 * In general it manages two kinds of Views:
 *  -> _one_ and only primaryView and
 *  -> _one_ and only secondaryView
 *
 * - one of each can be rendered anlongside each other
 * - toggling a secondary view shall not intervene with rendering the primary
 *   view, meaning that toggling a delete/-Block view (seconday) must not close
 *   the edit view (dirty changes in tinymce).
 * - renders "progressively enhanced" a views specified via the `activeView`
 *   property.
 * - the `default` (children) view is rendered outside the
 *   `block-interactions__content` div, whereas all other are rendered inside.
 */
export function BlockInteractions({
  contentWrapperDndProps,
  contentWrapperProps,
  isInLayoutGroup = false,
  alwaysShowInfo,
  views,
  activeView,
  uuid,
  elementType,
  headline,
  info,
  buttons = {},
  instantInteraction,
  refreshBlock,
  refreshCount = 0,
  children,
}: BlockInteractionsProps) {
  const headingRef = createRef<HTMLHeadingElement>();
  const id = idCreator(elementType, uuid);
  const { t } = useTranslation();
  const { editModeInfo } = useAppContext();

  const {
    state,
    setState,
    toggleActiveView,
    showDefaultView,
    viewExists,
    closeSecondaryView,
    getHref,
    getButtonModifier,
    getTitleText,
    getModalProps,
    getSecondaryViewProps,
    showInfoWithNoButtons,
    getHeadlinePosition,
  } = useBlockInteractions(
    headingRef,
    views,
    headline,
    id,
    elementType,
    uuid,
    t,
    buttons,
    contentWrapperProps,
    refreshBlock,
    alwaysShowInfo,
    activeView,
  );

  /**
   * Component Did Update
   * */
  useEffect(() => {
    if (refreshCount > 0) {
      setState(computeState(Views.DEFAULT));
      focusHeadlineFromRefAndOutlineBlock(headingRef);
    }
  }, [refreshCount]);

  return (
    <BlockInteractionContextProvider switchToDefaultView={showDefaultView}>
      <div
        className="block-interactions"
        id={id}
        data-index={getIndex(contentWrapperProps?.index, contentWrapperProps?.baseIndex, true)}
      >
        {showInfoWithNoButtons() && (
          <div
            className={bem(
              'block-interactions__top',
              editModeInfo?.active ? 'block-interactions__top--border-top' : '',
            )}
          >
            <BlockInteractionsBar contentWrapperDndProps={contentWrapperDndProps}>
              <BlockInteractionsLoadingSpinner uuid={uuid} contentWrapperProps={contentWrapperProps} />
              <BlockInteractionsInfo
                isInLayoutGroup={isInLayoutGroup}
                headline={headline}
                headingRef={headingRef}
                info={info}
                getHeadlinePosition={getHeadlinePosition}
              />
              <BlockInteractionsButtons
                state={state}
                buttons={buttons}
                views={views}
                id={id}
                uuid={uuid}
                elementType={elementType}
                contentWrapperProps={contentWrapperProps}
                getTitleText={getTitleText}
                getButtonModifier={getButtonModifier}
                getHref={getHref}
                isInLayoutGroup={isInLayoutGroup}
                toggleActiveView={toggleActiveView}
                t={t}
              />
            </BlockInteractionsBar>
            <ContentWrapperContainer
              uuid={uuid}
              elementType={elementType}
              contentWrapperProps={contentWrapperProps}
              t={t}
            />
            <BlockInteractionsInstantInteraction views={views} instantInteraction={instantInteraction} />
            <BlockInteractionsContent id={id}>
              <SecondaryView
                uuid={uuid}
                state={state}
                setState={setState}
                buttons={buttons}
                elementType={elementType}
                contentWrapperProps={contentWrapperProps}
                getSecondaryViewProps={getSecondaryViewProps}
                closeSecondaryView={closeSecondaryView}
                viewExists={viewExists}
                getModalProps={getModalProps}
              />
              <EditView state={state} buttons={buttons} />
            </BlockInteractionsContent>
          </div>
        )}
        <DefaultView state={state}>{children}</DefaultView>
      </div>
    </BlockInteractionContextProvider>
  );
}
